/**
 * Quritish kinetikasi (batch, konvektiv, yuzadan bug'lanish).
 *
 * MANBA: Kasatkin 15-bob ("Quritish tezligi", quritish egri chizig'i);
 *        Pavlov-Romankov-Noskov, "Quritish" (davrlar bo'yicha vaqt hisobi);
 *        Treybal, Mass-Transfer Operations, Ch. 12 (12.27: h = 14.3·G^0.8, SI);
 *        Perry's 8th ed., Sec. 12 (constant/falling rate periods).
 *
 * TENGLAMALAR
 *   O'zgarmas tezlik davri (X > Xc): yuzasi nam, T_yuza = T_wb
 *       N_c = h·(T − T_wb) / r(T_wb)                    [kg suv/(m²·s)]
 *   Pasayuvchi tezlik davri (X* < X ≤ Xc), chiziqli yaqinlashish:
 *       N = N_c · (X − X*)/(Xc − X*)
 *   Material bo'yicha:  dX/dt = −N·A/G_dry
 *   Material harorati (kvazistatsionar issiqlik balansi h·(T − T_m) = N·r):
 *       T_m = T − N·r/h   → o'zgarmas davrda T_wb, X → X* da T ga intiladi
 *   Analitik vaqtlar (k = N_c·A/G_dry, 1/s):
 *       t_c = (X1 − Xc)/k
 *       t_f = ((Xc − X*)/k) · ln((Xc − X*)/(X2 − X*))        (X2 > X*)
 *
 * ASSUMPTION:
 *   1. Havo holati (T, Y) quritgich bo'ylab o'zgarmas — havo katta ortiqchalikda (batch tovoq quritgich).
 *   2. Pasayuvchi tezlik chiziqli (Kasatkin, birinchi yaqinlashish); haqiqiy egri material turiga bog'liq.
 *   3. Material issiqlik sig'imi hisobga olinmagan (T_m kvazistatsionar) — isish vaqti e'tiborsiz.
 *   4. Nurlanish va tovoq devori orqali issiqlik o'tishi yo'q (faqat konvektiv h).
 *   5. h korrelyatsiyasi (Treybal) yuza ustidan parallel oqim, G = 0.7…8 kg/(m²·s) uchun.
 */

export const TREYBAL_C = 14.3;
export const TREYBAL_N = 0.8;

/** Treybal (SI): h = 14.3·G^0.8, G kg/(m²·s) → W/(m²·K) */
export function heatTransferCoefficientTreybal(G: number): number {
  return TREYBAL_C * Math.pow(G, TREYBAL_N);
}

/** O'zgarmas tezlik N_c, kg/(m²·s) */
export function constantDryingRate(h: number, T: number, Twb: number, rWb: number): number {
  return (h * Math.max(0, T - Twb)) / rWb;
}

export interface KineticsParams {
  Nc: number;
  Xc: number;
  Xeq: number;
}

/** Joriy tezlik N(X), kg/(m²·s) */
export function dryingRate(X: number, k: KineticsParams): number {
  if (X >= k.Xc) return k.Nc;
  if (X <= k.Xeq) return 0;
  return (k.Nc * (X - k.Xeq)) / (k.Xc - k.Xeq);
}

/**
 * X dan X2 gacha quritish uchun qolgan vaqt (analitik), s.
 * kRate = N_c·A/G_dry (1/s). X2 ≤ X* → Infinity; X ≤ X2 → 0.
 */
export function timeToMoisture(X: number, X2: number, k: KineticsParams, kRate: number): number {
  if (X <= X2) return 0;
  if (!(kRate > 0)) return Infinity;
  const span = k.Xc - k.Xeq;
  let t = 0;
  let x = X;
  if (x > k.Xc) {
    const target = Math.max(X2, k.Xc);
    t += (x - target) / kRate;
    x = target;
  }
  if (x > X2) {
    if (X2 <= k.Xeq) return Infinity;
    t += (span / kRate) * Math.log((x - k.Xeq) / (X2 - k.Xeq));
  }
  return t;
}
